import React from "react";
import {
    FaTimes,
    FaChevronDown,
    FaFilter,
    FaUndo,
} from "react-icons/fa";

// export interface FilterOption {
//   label: string;
//   value: string;
// }

export interface FilterField {
    key: string;
    label: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
}

interface FilterModalProps {
    isOpen: boolean;
    title?: string;
    description?: string;
    fields: FilterField[];
    onApply: () => void;
    onCancel: () => void;
    onClear: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    title = "Filter",
    description = "Choose the filters you want to apply.",
    fields,
    onApply,
    onCancel,
    onClear,
}) => {
    if (!isOpen) return null;

    const activeFilters = fields.filter(
        (field) =>
            field.value &&
            !field.value.toLowerCase().startsWith("all")
    ).length;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/40 p-4"
            onClick={onCancel}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="filter-modal-title"
                className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className="flex items-start justify-between px-6 py-5">
                    <div className="flex items-start gap-3.5">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
                            {FaFilter({ className: "text-sm" })}
                        </div>

                        <div>
                            <div className="flex items-center gap-2">
                                <h2
                                    id="filter-modal-title"
                                    className="text-base font-semibold tracking-tight text-slate-900"
                                >
                                    {title}
                                </h2>

                                {activeFilters > 0 && (
                                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">
                                        {activeFilters} active
                                    </span>
                                )}
                            </div>

                            <p className="mt-1 text-xs leading-5 text-slate-500">
                                {description}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close filter"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    >
                        {FaTimes({})}
                    </button>
                </div>

                {/* DIVIDER */}
                <div className="border-t border-slate-100" />

                {/* CONTENT */}
                <div className="space-y-1 px-6 py-5">
                    {fields.map((field) => {
                        const isActive =
                            field.value &&
                            !field.value.toLowerCase().startsWith("all");

                        return (
                            <div
                                key={field.key}
                                className="rounded-xl p-3 transition hover:bg-slate-50"
                            >
                                <div className="mb-2.5 flex items-center justify-between">
                                    <label
                                        htmlFor={`filter-${field.key}`}
                                        className="text-sm font-medium text-slate-700"
                                    >
                                        {field.label}
                                    </label>

                                    {isActive && (
                                        <span className="text-[11px] font-medium text-blue-600">
                                            Selected
                                        </span>
                                    )}
                                </div>

                                <div className="relative">
                                    <select
                                        id={`filter-${field.key}`}
                                        value={field.value}
                                        onChange={(e) =>
                                            field.onChange(e.target.value)
                                        }
                                        className={`h-11 w-full appearance-none rounded-xl border px-3.5 pr-10 text-sm outline-none transition ${isActive
                                                ? "border-blue-200 bg-blue-50/40 text-slate-800 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                                                : "border-slate-200 bg-slate-50 text-slate-500 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                                            }`}
                                    >
                                        {field.options.map((option) => (
                                            <option
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </option>
                                        ))}
                                    </select>

                                    {FaChevronDown({
                                        className: `pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[10px] ${isActive
                                                ? "text-blue-500"
                                                : "text-slate-400"
                                            }`
                                        })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* FOOTER */}
                <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                    <div className="flex items-center justify-between gap-3">
                        {/* CLEAR */}
                        <button
                            type="button"
                            onClick={onClear}
                            disabled={activeFilters === 0}
                            className="inline-flex h-10 items-center gap-2 rounded-xl px-3 text-xs font-medium text-slate-500 transition hover:bg-white hover:text-slate-800 disabled:pointer-events-none disabled:opacity-40"
                        >
                            {FaUndo ({className:"text-[10px]" })}
                            Clear filters
                        </button>

                        {/* ACTIONS */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={onApply}
                                className="h-10 rounded-xl bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.98]"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterModal;